import { ReactNode } from 'react';
import { Navigate } from '@tanstack/react-router';
import { ShieldX } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission, type UserRole } from '@/types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface AdminGuardProps {
  children: ReactNode;
  fallback?: ReactNode | undefined;
  redirectTo?: string | undefined;
}

function hasAdminPermission(roles: UserRole[]): boolean {
  return roles.some((role) => hasPermission(role, 'admin'));
}

export function AdminGuard({ children, fallback, redirectTo }: AdminGuardProps) {
  const { user, isLoading, isAuthenticated } = useAuth();

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" label="Checking permissions..." />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    if (redirectTo) {
      return <Navigate to={redirectTo} />;
    }
    return <Navigate to="/" />;
  }

  // Check admin permission
  const isAdmin = hasAdminPermission(user.roles);

  if (!isAdmin) {
    // Use custom fallback if provided
    if (fallback) {
      return <>{fallback}</>;
    }

    // Default unauthorized view
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <Card className="max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <ShieldX className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You don&apos;t have permission to access this area. Admin privileges are required.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground mb-4">
              If you believe this is an error, please contact your administrator.
            </p>
            <Button asChild>
              <a href="/">Return to Dashboard</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is admin - render children
  return <>{children}</>;
}
